import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Globe, ExternalLink, Users, MapPin, Shield, Gamepad2, Edit2, Trash2, ChevronDown, ChevronUp, FileText, Loader2, RefreshCw } from 'lucide-react';
import FaviconImage from '../common/FaviconImage';
import { selectIsBrandProcessing } from '../../store/slices/termsCheckerSlice';

const TermsCheckerCard = ({ project, onClick, onEdit, onDelete, onViewAffiliates, onReclassify }) => {
  const [showTerms, setShowTerms] = useState(false);
  const [isReclassifying, setIsReclassifying] = useState(false);
  const isProcessing = useSelector(selectIsBrandProcessing(project.id));

  // Parse verticals from JSON string if needed
  const verticals = typeof project.verticals === 'string'
    ? JSON.parse(project.verticals || '[]')
    : (project.verticals || []);

  // Parse vertical_terms from JSON string if needed
  const verticalTerms = typeof project.vertical_terms === 'string'
    ? JSON.parse(project.vertical_terms || '{}')
    : (project.vertical_terms || {});

  const handleExternalLinkClick = (e) => {
    e.stopPropagation();
    let url = project.brand_url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    window.open(url, '_blank');
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit(project);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(project);
  };

  const toggleTerms = (e) => {
    e.stopPropagation();
    setShowTerms(!showTerms);
  };

  const handleViewAffiliatesClick = (e) => {
    e.stopPropagation();
    if (isProcessing) return; // Don't allow click while processing
    if (onViewAffiliates) onViewAffiliates(project);
  };

  const handleReclassifyClick = async (e) => {
    e.stopPropagation();
    if (isReclassifying || isProcessing) return;

    if (!window.confirm(`Reclassify existing backlinks for "${project.brand_name}"? This will skip fetching new backlinks from SEMrush and use existing data.`)) {
      return;
    }

    setIsReclassifying(true);
    try {
      const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
      const response = await fetch(`${basePath}/api/terms-checker/reclassify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ termsCheckerId: project.id })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert(`Reclassification started for ${data.data?.totalBacklinks || 0} backlinks. Check console for progress.`);
        if (onReclassify) onReclassify(project);
      } else {
        alert(data.message || 'Failed to start reclassification');
      }
    } catch (error) {
      console.error('Error starting reclassification:', error);
      alert('Failed to start reclassification. Please try again.');
    } finally {
      setIsReclassifying(false);
    }
  };

  // Get display URL (without protocol)
  const displayUrl = project.brand_url?.replace(/^https?:\/\//, '').replace(/\/$/, '');

  // Check if there are any terms to display
  const hasTerms = Object.keys(verticalTerms).length > 0 && Object.values(verticalTerms).some(term => term && term.trim());

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 transition-all duration-300 group relative overflow-hidden transform hover:border-blue-300 hover:shadow-sm hover:-translate-y-0.5 cursor-pointer"
    >
      <div className="relative p-4">
        {/* Header - Favicon, Brand Name, URL, Action Buttons */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="relative flex items-center gap-2">
              <div className="w-8 h-8 rounded-md flex items-center justify-center overflow-hidden bg-gray-50">
                <FaviconImage
                  domain={project.brand_url}
                  size={32}
                  className="rounded-md"
                />
              </div>
            </div>
            <div>
              <h3 className="text-base font-medium text-blue-600 mb-0.5">
                {project.brand_name}
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Globe className="w-3 h-3 text-blue-600" />
                  <span>{displayUrl}</span>
                  <ExternalLink
                    className="w-3 h-3 cursor-pointer text-blue-600 hover:text-blue-800 transition-colors"
                    onClick={handleExternalLinkClick}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={handleReclassifyClick}
              disabled={isReclassifying || isProcessing}
              className={`p-1.5 rounded-md transition-colors ${
                isReclassifying || isProcessing
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
              }`}
              title="Reclassify (bypass SEMrush)"
            >
              <RefreshCw className={`w-4 h-4 ${isReclassifying ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleEditClick}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDeleteClick}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="h-px bg-gray-200 mb-4"></div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Live Affiliates Count - Clickable */}
          <div
            onClick={handleViewAffiliatesClick}
            className={`border rounded-lg p-3 transition-all duration-300 ${
              isProcessing
                ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-75'
                : 'bg-blue-50 border-blue-200 hover:scale-105 hover:shadow-sm hover:bg-blue-100 cursor-pointer'
            }`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              {isProcessing ? (
                <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
              ) : (
                <Users className="w-4 h-4 text-blue-600" />
              )}
              <div className={`text-sm font-medium ${isProcessing ? 'text-gray-500' : 'text-blue-700'}`}>
                {isProcessing ? 'Processing...' : (project.live_affiliates_count || project.affiliates_count || 0)}
              </div>
            </div>
            <div className={`text-xs font-medium ${isProcessing ? 'text-gray-500' : 'text-blue-600'}`}>
              {isProcessing ? (
                'Please wait, up to 2 min'
              ) : (
                <>
                  Live Affiliates
                  <ExternalLink className="w-3 h-3 inline-block ml-1" />
                </>
              )}
            </div>
          </div>

          {/* Country */}
          <div className="border rounded-lg p-3 bg-gray-50 border-gray-200 transition-all duration-300 hover:scale-105 hover:shadow-sm">
            <div className="flex items-center gap-2 mb-1.5">
              <MapPin className="w-4 h-4 text-green-600" />
              <div className="text-sm font-medium text-gray-700">
                {project.country || 'N/A'}
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500">
              Country
            </div>
          </div>

          {/* Regulatory Market */}
          <div className="border rounded-lg p-3 bg-gray-50 border-gray-200 transition-all duration-300 hover:scale-105 hover:shadow-sm">
            <div className="flex items-center gap-2 mb-1.5">
              <Shield className="w-4 h-4 text-purple-600" />
              <div className="text-sm font-medium text-gray-700">
                {project.regulatory_market || 'N/A'}
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500">
              Regulatory Market
            </div>
          </div>

          {/* Verticals */}
          <div className="border rounded-lg p-3 bg-gray-50 border-gray-200 transition-all duration-300 hover:scale-105 hover:shadow-sm">
            <div className="flex items-center gap-2 mb-1.5">
              <Gamepad2 className="w-4 h-4 text-orange-600" />
              <div className="text-sm font-medium text-gray-700">
                {verticals.length > 0 ? verticals.length : 0}
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500">
              Verticals
            </div>
          </div>
        </div>

        {/* Verticals Tags */}
        {verticals.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {verticals.map((vertical, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-md border border-blue-200"
              >
                {vertical.charAt(0).toUpperCase() + vertical.slice(1)}
              </span>
            ))}
          </div>
        )}

        {/* Reclassify Button - More Visible */}
        <div className="mt-3">
          <button
            onClick={handleReclassifyClick}
            disabled={isReclassifying || isProcessing}
            className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isReclassifying || isProcessing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${isReclassifying ? 'animate-spin' : ''}`} />
            {isReclassifying ? 'Reclassifying...' : 'Reclassify (Skip SEMrush)'}
          </button>
        </div>

        {/* Terms Toggle Button */}
        {hasTerms && (
          <div className="mt-3">
            <button
              onClick={toggleTerms}
              className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-600 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-500" />
                <span>View Terms</span>
              </div>
              {showTerms ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>
        )}

        {/* Terms Content (Expandable) */}
        {showTerms && hasTerms && (
          <div className="mt-3 space-y-3">
            {Object.entries(verticalTerms).map(([vertical, terms]) => {
              if (!terms || !terms.trim()) return null;
              return (
                <div key={vertical} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                      {vertical.charAt(0).toUpperCase() + vertical.slice(1)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap break-words">
                    {terms}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TermsCheckerCard;
